import React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { AddCircle, Delete, DragIndicator, ExpandLess, ExpandMore, Refresh } from '@mui/icons-material';
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';

import { usePersistedSetting, usePersistedSize, useToastService, uuid } from '@@common';

import { ContextMetadataModel, EnumModel } from '../../../../api/api';
import CopyToClipboardButton from '../../../../components/Clipboard/CopyToClipboardButton';
import DialogHeaderActions from '../../../../components/DialogHeaderActions';
import { EnumReference } from '../../Models/EnumReference';
import { JSON_SCHEMA_FORMATS, JSON_SCHEMA_TYPES, JsonSchemaEnumReference, JsonSchemaModel, JsonSchemaType } from '../../Models/JsonSchemaModel';
import { SchemaValueModel } from '../../Models/SchemaValueModel';
import { readSchemaEditorCollection, writeSchemaEditorCollection } from '../../Services/SchemaEditorCollection';
import { EnumerationInputDialog } from './EnumerationInputDialog';

type SchemaValueInputDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	schemaValue: SchemaValueModel | undefined;
	onSchemaValueChange: (schemaValue: SchemaValueModel | undefined) => void;
	sharedSchemaActions?: {
		schemas: SchemaValueModel[];
		selectedSchemaId?: string;
		onSelect: (id: string) => void;
		onMakeIndependentCopy?: (schema: SchemaValueModel) => void;
		onSaveAsSharedSchema?: (schema: SchemaValueModel) => void;
	};
};

type PropertyRow = {
	id: string;
	name: string;
	type: JsonSchemaModel['type'];
	format?: string;
	description: string;
	isRequired: boolean;
	isMultiple: boolean;
	isNullableCollection?: boolean;
	enumValues: (string | number)[];
	enumRef?: JsonSchemaEnumReference;
	items?: JsonSchemaModel;
	nestedProperties?: PropertyRow[];
};

type TypeOption = {
	label: string;
	type: JsonSchemaModel['type'];
	format?: string;
};

const TYPE_OPTIONS: TypeOption[] = [
	{ label: 'Guid', type: JSON_SCHEMA_TYPES.STRING, format: JSON_SCHEMA_FORMATS.UUID },
	{ label: 'DateTime', type: JSON_SCHEMA_TYPES.STRING, format: JSON_SCHEMA_FORMATS.DATE_TIME },
	{ label: 'Date', type: JSON_SCHEMA_TYPES.STRING, format: JSON_SCHEMA_FORMATS.DATE },
	{ label: 'String', type: JSON_SCHEMA_TYPES.STRING },
	{ label: 'Number', type: JSON_SCHEMA_TYPES.NUMBER },
	{ label: 'Integer', type: JSON_SCHEMA_TYPES.INTEGER },
	{ label: 'Boolean', type: JSON_SCHEMA_TYPES.BOOLEAN },
	{ label: 'Object', type: JSON_SCHEMA_TYPES.OBJECT },
	{ label: 'Null', type: JSON_SCHEMA_TYPES.NULL },
];

const getNonNullType = (type: JsonSchemaModel['type']): JsonSchemaType | undefined => {
	if (!Array.isArray(type)) return type;
	return type.length === 2 && type.includes(JSON_SCHEMA_TYPES.NULL)
		? type.find((value) => value !== JSON_SCHEMA_TYPES.NULL)
		: undefined;
};

const isNullableType = (type: JsonSchemaModel['type']): boolean =>
	Array.isArray(type) && type.length === 2 && type.includes(JSON_SCHEMA_TYPES.NULL);

const DIALOG_SIZE_KEY = 'schemaValueInputDialog.size';
const DIALOG_MAXIMIZED_KEY = 'schemaValueInputDialog.maximized';

// Types that support enum values
const ENUM_SUPPORTED_TYPES: string[] = [JSON_SCHEMA_TYPES.STRING, JSON_SCHEMA_TYPES.NUMBER, JSON_SCHEMA_TYPES.INTEGER];

// Helper function to check if a property supports enum values
const canHaveEnum = (property: PropertyRow): boolean => {
	const type = getNonNullType(property.type);
	if (!type || !ENUM_SUPPORTED_TYPES.includes(type)) {
		return false;
	}
	// String type with format (e.g., uuid, date-time) cannot have enums
	if (type === JSON_SCHEMA_TYPES.STRING && property.format) {
		return false;
	}
	return true;
};

// Helper function to get the select value key for a type/format combination
const getOptionKey = (type: JsonSchemaModel['type'], format?: string): string => {
	const nonNullType = getNonNullType(type);
	const option = TYPE_OPTIONS.find((opt) => {
		// Match both type and format (or lack of format)
		const typeMatches = opt.type === nonNullType;
		const formatMatches = opt.format === format || (!opt.format && !format);
		return typeMatches && formatMatches;
	});
	const key = String(option ? option.format || option.type : (nonNullType ?? ''));
	return isNullableType(type) ? `nullable:${key}` : key;
};

// Helper function to find option by key
const getOptionByKey = (key: string): TypeOption | undefined => {
	const nullable = key.startsWith('nullable:');
	const baseKey = nullable ? key.slice('nullable:'.length) : key;
	const option = TYPE_OPTIONS.find((opt) => (opt.format || opt.type) === baseKey);
	return option && nullable ? { ...option, type: [option.type as JsonSchemaType, JSON_SCHEMA_TYPES.NULL] } : option;
};

function PropertyRows({
	properties,
	parentPath = [],
	level = 0,
	expandedProperties,
	onToggleExpanded,
	onNameChange,
	onTypeChange,
	onMultipleChange,
	onRequiredChange,
	onDescriptionChange,
	editingEnumProperty,
	enumInputValue,
	onEnumInputChange,
	onEditingChange,
	onAddEnum,
	onDeleteEnum,
	enumPickerPath,
	onOpenEnumPicker,
	onEnumSelected,
	onClearEnumRef,
	onDelete,
	onReorder,
	dragOverPropertyId,
	onDragOverProperty,
	onClearDragOverProperty,
	onAddProperty,
}: {
	properties: PropertyRow[];
	parentPath?: string[];
	level?: number;
	expandedProperties: Set<string>;
	onToggleExpanded: (propertyId: string) => void;
	onNameChange: (path: string[], level: number, value: string) => void;
	onTypeChange: (path: string[], level: number, type: JsonSchemaModel['type'], format?: string) => void;
	onMultipleChange: (path: string[], level: number, value: boolean) => void;
	onRequiredChange: (path: string[], level: number, value: boolean) => void;
	onDescriptionChange: (path: string[], level: number, value: string) => void;
	editingEnumProperty: string | null;
	enumInputValue: string;
	onEnumInputChange: (value: string) => void;
	onEditingChange: (propertyId: string | null) => void;
	onAddEnum: (path: string[], property: PropertyRow) => void;
	onDeleteEnum: (path: string[], property: PropertyRow, index: number) => void;
	enumPickerPath: string[] | null;
	onOpenEnumPicker: (path: string[] | null) => void;
	onEnumSelected: (path: string[], property: PropertyRow, enumModel: EnumModel | undefined, context?: ContextMetadataModel | undefined) => void;
	onClearEnumRef: (path: string[], property: PropertyRow) => void;
	onDelete: (path: string[], level: number, propertyId: string) => void;
	onReorder: (sourceId: string, targetId: string) => void;
	dragOverPropertyId: string | null;
	onDragOverProperty: (id: string) => void;
	onClearDragOverProperty: () => void;
	onAddProperty: (parentPath: string[]) => void;
}) {
	return (
		<>
			{properties.map((property, index) => {
				const currentPath = [...parentPath, property.id];
				const isExpanded = expandedProperties.has(property.id);
				const hasNested = getNonNullType(property.type) === JSON_SCHEMA_TYPES.OBJECT;
				const enumPickerOpen = enumPickerPath?.join('/') === currentPath.join('/');
				const enumRef: EnumReference | undefined = property.enumRef
					? { Id: property.enumRef.id, Name: property.enumRef.name, ContextId: property.enumRef.contextId }
					: undefined;

				return (
					<React.Fragment key={property.id}>
						<TableRow
							onDragOver={
								level === 0
									? (event) => {
										event.preventDefault();
										onDragOverProperty(property.id);
									}
									: undefined
							}
							onDragLeave={level === 0 ? onClearDragOverProperty : undefined}
							onDrop={
								level === 0
									? (event) => {
										event.preventDefault();
										const sourceId = event.dataTransfer.getData('text/plain');
										if (sourceId) {
											onReorder(sourceId, property.id);
										}
										onClearDragOverProperty();
									}
									: undefined
							}
							sx={{
								backgroundColor: hasNested ? 'action.hover' : level > 0 && index % 2 === 1 ? 'transparent' : 'background.paper',
								borderTop: dragOverPropertyId === property.id ? '2px solid' : undefined,
								borderColor: dragOverPropertyId === property.id ? 'primary.main' : undefined,
								transition: 'background-color 120ms ease, border-color 120ms ease',
								'&:hover': { backgroundColor: 'action.hover' },
							}}
						>
							{/* Property Name Cell */}
							<TableCell sx={{ paddingLeft: `${level * 32 + 16}px` }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									{level === 0 && (
										<Tooltip title="Drag to reorder">
											<IconButton
												size="small"
												draggable
												aria-label={`Reorder ${property.name || 'property'}`}
												onDragStart={(event) => {
													event.dataTransfer.effectAllowed = 'move';
													event.dataTransfer.setData('text/plain', property.id);
												}}
												onDragEnd={onClearDragOverProperty}
											>
												<DragIndicator fontSize="small" />
											</IconButton>
										</Tooltip>
									)}
									{hasNested && (
										<IconButton size="small" onClick={() => onToggleExpanded(property.id)}>
											{isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
										</IconButton>
									)}
									<TextField
										value={property.name}
										onChange={(e) => onNameChange(currentPath, level, e.target.value)}
										size="small"
										fullWidth
										placeholder="propertyName"
									/>
								</Box>
							</TableCell>

							{/* Type Cell */}
							<TableCell>
								<Select
									value={getOptionKey(property.type, property.format)}
									onChange={(e) => {
										const selectedValue = e.target.value;
										const option = getOptionByKey(selectedValue);
										if (option) {
											onTypeChange(currentPath, level, option.type, option.format);
										}
									}}
									size="small"
									fullWidth
								>
									{TYPE_OPTIONS.map((option) => (
										<MenuItem key={option.label} value={option.format || option.type}>
											{option.label}
										</MenuItem>
									))}
									{TYPE_OPTIONS.filter((option) => option.type !== JSON_SCHEMA_TYPES.NULL && option.type !== JSON_SCHEMA_TYPES.ARRAY).map((option) => (
										<MenuItem key={`${option.label}-nullable`} value={`nullable:${option.format || option.type}`}>
											{option.label} (nullable)
										</MenuItem>
									))}
								</Select>
								{property.isMultiple && property.isNullableCollection && <Typography variant="caption" color="text.secondary">Nullable collection</Typography>}
							</TableCell>

							{/* Multiple Cell */}
							<TableCell sx={{ width: 76, minWidth: 76, maxWidth: 76, textAlign: 'center', px: 0.5 }}>
								<Checkbox checked={property.isMultiple} onChange={(e) => onMultipleChange(currentPath, level, e.target.checked)} size="small" />
							</TableCell>

							{/* Required Cell */}
							<TableCell sx={{ width: 76, minWidth: 76, maxWidth: 76, textAlign: 'center', px: 0.5 }}>
								<Checkbox checked={property.isRequired} onChange={(e) => onRequiredChange(currentPath, level, e.target.checked)} size="small" />
							</TableCell>

							{/* Description Cell */}
							<TableCell>
								<TextField
									value={property.description}
									onChange={(e) => onDescriptionChange(currentPath, level, e.target.value)}
									size="small"
									fullWidth
									placeholder="Description"
									multiline
									maxRows={3}
								/>
							</TableCell>

							{/* Enum Cell */}
							<TableCell>
								{canHaveEnum(property) && (
									<Stack direction="column" spacing={1} alignItems="flex-start">
										<Stack direction="row" spacing={1} alignItems="center">
											{property.enumRef && (
												<Chip
													label={property.enumRef.name || property.enumRef.id}
													size="small"
													color="primary"
													onDelete={() => onClearEnumRef(currentPath, property)}
												/>
											)}
											<Button size="small" onClick={() => onOpenEnumPicker(currentPath)} sx={{ px: 0, minWidth: 0, justifyContent: 'flex-start' }}>
												{property.enumRef ? 'Change Enum' : 'Link Enum'}
											</Button>
											{enumPickerOpen && (
												<EnumerationInputDialog
													open={enumPickerOpen}
													setOpen={(nextOpen) => {
														if (!nextOpen) {
															onOpenEnumPicker(null);
														}
													}}
													enumRef={enumRef}
													onEnumRefChange={() => undefined}
													onEnumSelected={(enumModel, context) => onEnumSelected(currentPath, property, enumModel, context)}
												/>
											)}
										</Stack>
							{property.enumValues.map((enumValue, idx) => (
								<Chip
									key={idx}
									label={String(enumValue)}
									size="small"
									onDelete={() => onDeleteEnum(currentPath, property, idx)}
									sx={{
										width: '100%',
										justifyContent: 'space-between',
										'& .MuiChip-label': { flexGrow: 1, textAlign: 'left' },
										'& .MuiChip-deleteIcon': { ml: 1, mr: 0.5 },
									}}
								/>
							))}
										{editingEnumProperty === property.id ? (
											<Box sx={{ display: 'flex', gap: 1 }}>
												<TextField
													value={enumInputValue}
													onChange={(e) => onEnumInputChange(e.target.value)}
													size="small"
													placeholder="Add enum value"
													onKeyDown={(e) => {
														if (e.key === 'Enter') {
															onAddEnum(currentPath, property);
														}
													}}
												/>
												<IconButton size="small" onClick={() => onAddEnum(currentPath, property)}>
													<AddCircle fontSize="small" />
												</IconButton>
												<IconButton size="small" onClick={() => onEditingChange(null)}>
													<Delete fontSize="small" />
												</IconButton>
											</Box>
										) : (
											<Button
												size="small"
												startIcon={<AddCircle />}
												sx={{ px: 0, minWidth: 0, justifyContent: 'flex-start' }}
												onClick={() => {
													onEditingChange(property.id);
													onEnumInputChange('');
												}}
											>
												Add Enum
											</Button>
										)}
									</Stack>
								)}
							</TableCell>

							{/* Actions Cell */}
							<TableCell sx={{ width: 56, minWidth: 56, maxWidth: 56, textAlign: 'center', px: 0.25 }}>
								<Box sx={{ display: 'flex', justifyContent: 'center' }}>
									<IconButton size="small" onClick={() => onDelete(currentPath, level, property.id)} color="error">
										<Delete fontSize="small" />
									</IconButton>
								</Box>
							</TableCell>
						</TableRow>
						{hasNested && isExpanded && (
							<TableRow>
								<TableCell colSpan={7} sx={{ p: 0, borderBottom: 0 }}>
									<Collapse in={isExpanded} timeout="auto" unmountOnExit>
										<Box
											sx={{
												my: 1.25,
												ml: { xs: 1, sm: 3 },
												mr: 1.5,
												border: 1,
												borderLeft: '3px solid',
												borderColor: 'divider',
												borderLeftColor: 'primary.main',
												borderRadius: 1.5,
												backgroundColor: 'background.default',
												overflow: 'hidden',
											}}
										>
											<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
												<Stack direction="row" spacing={1} alignItems="center">
													<Typography variant="subtitle2" color="text.secondary">
														Nested properties
													</Typography>
													<Chip label={property.nestedProperties?.length ?? 0} size="small" variant="outlined" />
												</Stack>
												<Button size="small" startIcon={<AddCircle />} onClick={() => onAddProperty(currentPath)}>
													Add property
												</Button>
											</Stack>
											<Table size="small" sx={{ '& .MuiTableCell-root': { borderBottomColor: 'divider' } }}>
												<TableBody>
													{property.nestedProperties && property.nestedProperties.length > 0 ? (
														<PropertyRows
															properties={property.nestedProperties}
															parentPath={currentPath}
															level={level + 1}
															expandedProperties={expandedProperties}
															onToggleExpanded={onToggleExpanded}
															onNameChange={onNameChange}
															onTypeChange={onTypeChange}
															onMultipleChange={onMultipleChange}
															onRequiredChange={onRequiredChange}
															onDescriptionChange={onDescriptionChange}
															editingEnumProperty={editingEnumProperty}
															enumInputValue={enumInputValue}
															onEnumInputChange={onEnumInputChange}
															onEditingChange={onEditingChange}
															onAddEnum={onAddEnum}
															onDeleteEnum={onDeleteEnum}
															enumPickerPath={enumPickerPath}
															onOpenEnumPicker={onOpenEnumPicker}
															onEnumSelected={onEnumSelected}
															onClearEnumRef={onClearEnumRef}
															onDelete={onDelete}
																onReorder={onReorder}
															dragOverPropertyId={dragOverPropertyId}
															onDragOverProperty={onDragOverProperty}
															onClearDragOverProperty={onClearDragOverProperty}
															onAddProperty={onAddProperty}
														/>
													) : (
														<TableRow>
															<TableCell colSpan={7} sx={{ textAlign: 'center', fontStyle: 'italic', color: 'text.secondary' }}>
																No properties defined. Click "Add property" to add one.
															</TableCell>
														</TableRow>
													)}
												</TableBody>
											</Table>
										</Box>
									</Collapse>
								</TableCell>
							</TableRow>
						)}
					</React.Fragment>
				);
			})}
		</>
	);
}

export function SchemaValueInputDialog({ open, setOpen, schemaValue, onSchemaValueChange, sharedSchemaActions }: SchemaValueInputDialogProps) {
	const toastService = useToastService();
	const [isMaximized, setIsMaximized] = usePersistedSetting(DIALOG_MAXIMIZED_KEY, false);
	const { ref: paperRef, size: storedSize } = usePersistedSize(DIALOG_SIZE_KEY, open);
	const [schemaId, setSchemaId] = useState(schemaValue?.Id || uuid());
	const [title, setTitle] = useState(schemaValue?.Value?.title || '');
	const [description, setDescription] = useState(schemaValue?.Value?.description || '');
	const [properties, setProperties] = useState<PropertyRow[]>([]);
	const [dragOverPropertyId, setDragOverPropertyId] = useState<string | null>(null);
	const [editingEnumProperty, setEditingEnumProperty] = useState<string | null>(null);
	const [enumInputValue, setEnumInputValue] = useState('');
	const [enumPickerPath, setEnumPickerPath] = useState<string[] | null>(null);
	const [expandedProperties, setExpandedProperties] = useState<Set<string>>(new Set());
	const [detailsExpanded, setDetailsExpanded] = useState(() => !!(schemaValue?.Value?.title || schemaValue?.Value?.description));

	useEffect(() => {
		if (schemaValue?.Id) {
			setSchemaId(schemaValue.Id);
		}
		setTitle(schemaValue?.Value?.title || '');
		setDescription(schemaValue?.Value?.description || '');
	}, [schemaValue]);

	useEffect(() => {
		const convertToPropertyRows = (schema: JsonSchemaModel, requiredFields?: string[]): PropertyRow[] => {
			if (!schema.properties) return [];
			return Object.entries(schema.properties).map(([name, propSchema]) => {
				const { isMultiple, isNullableCollection, itemSchema: actualSchema } = readSchemaEditorCollection(propSchema);

				const nestedProps =
					getNonNullType(actualSchema.type) === JSON_SCHEMA_TYPES.OBJECT && actualSchema.properties ? convertToPropertyRows(actualSchema, actualSchema.required) : undefined;

				return {
					id: name,
					name,
					type: actualSchema.type,
					format: actualSchema.format,
					description: propSchema.description || '',
					isRequired: requiredFields?.includes(name) || false,
					isMultiple,
					isNullableCollection,
					enumValues: actualSchema.enum || [],
					enumRef: actualSchema['x-enumRef'],
					nestedProperties: nestedProps,
				};
			});
		};

		if (schemaValue?.Value?.properties) {
			const props = convertToPropertyRows(schemaValue.Value, schemaValue.Value.required);
			setProperties(props);

			// Auto-expand object properties
			const objectIds = new Set<string>();
			const collectObjectIds = (properties: PropertyRow[]) => {
				properties.forEach((prop) => {
					if (getNonNullType(prop.type) === JSON_SCHEMA_TYPES.OBJECT && prop.nestedProperties) {
						objectIds.add(prop.id);
						collectObjectIds(prop.nestedProperties);
					}
				});
			};
			collectObjectIds(props);
			setExpandedProperties(objectIds);
		}
	}, [schemaValue]);

	const handleAddProperty = useCallback((parentPath: string[] = []) => {
		const newProperty: PropertyRow = {
			id: `prop_${Date.now()}`,
			name: '',
			format: undefined,
			type: JSON_SCHEMA_TYPES.STRING,
			description: '',
			isRequired: false,
			isMultiple: false,
			enumValues: [],
		};

		if (parentPath.length === 0) {
			setProperties((prevProperties) => [...prevProperties, newProperty]);
		} else {
			const updateNested = (props: PropertyRow[], path: string[]): PropertyRow[] => {
				if (path.length === 1) {
					return props.map((p) => (p.id === path[0] ? { ...p, nestedProperties: [...(p.nestedProperties || []), newProperty] } : p));
				}
				return props.map((p) => (p.id === path[0] ? { ...p, nestedProperties: updateNested(p.nestedProperties || [], path.slice(1)) } : p));
			};
			setProperties((prevProperties) => updateNested(prevProperties, parentPath));
		}
	}, []);

	const toggleExpanded = useCallback((propertyId: string) => {
		setExpandedProperties((prev) => {
			const next = new Set(prev);
			if (next.has(propertyId)) {
				next.delete(propertyId);
			} else {
				next.add(propertyId);
			}
			return next;
		});
	}, []);

	const handleNestedPropertyChange = useCallback((path: string[], field: keyof PropertyRow, value: any) => {
		const updateNested = (props: PropertyRow[], currentPath: string[]): PropertyRow[] => {
			if (currentPath.length === 1) {
				return props.map((p) => {
					if (p.id === currentPath[0]) {
						const updated = { ...p, [field]: value };
						// Initialize nested properties if type changes to object
						if (field === 'type' && getNonNullType(value) === JSON_SCHEMA_TYPES.OBJECT && !updated.nestedProperties) {
							updated.nestedProperties = [];
							// Auto-expand when creating object type
							setExpandedProperties((prev) => new Set(prev).add(p.id));
						}
						return updated;
					}
					return p;
				});
			}
			return props.map((p) => (p.id === currentPath[0] ? { ...p, nestedProperties: updateNested(p.nestedProperties || [], currentPath.slice(1)) } : p));
		};
		setProperties((prevProperties) => updateNested(prevProperties, path));
	}, []);

	const handleDeleteNestedProperty = useCallback((path: string[]) => {
		const deleteNested = (props: PropertyRow[], currentPath: string[]): PropertyRow[] => {
			if (currentPath.length === 1) {
				return props.filter((p) => p.id !== currentPath[0]);
			}
			return props.map((p) => (p.id === currentPath[0] ? { ...p, nestedProperties: deleteNested(p.nestedProperties || [], currentPath.slice(1)) } : p));
		};
		setProperties((prevProperties) => deleteNested(prevProperties, path));
	}, []);

	const handleDeleteProperty = useCallback((id: string) => {
		setProperties((prevProperties) => prevProperties.filter((p) => p.id !== id));
	}, []);

	const handleReorderProperty = useCallback((sourceId: string, targetId: string) => {
		setProperties((prevProperties) => {
			const sourceIndex = prevProperties.findIndex((property) => property.id === sourceId);
			const targetIndex = prevProperties.findIndex((property) => property.id === targetId);
			if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
				return prevProperties;
			}

			const reorderedProperties = [...prevProperties];
			const [property] = reorderedProperties.splice(sourceIndex, 1);
			const insertionIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
			reorderedProperties.splice(insertionIndex, 0, property);
			return reorderedProperties;
		});
		setDragOverPropertyId(null);
	}, []);

	const handlePropertyChange = useCallback((id: string, field: keyof PropertyRow, value: any) => {
		setProperties((prevProperties) =>
			prevProperties.map((p) => {
				if (p.id === id) {
					const updated = { ...p, [field]: value };
					// Initialize nested properties if type changes to object
					if (field === 'type' && getNonNullType(value) === JSON_SCHEMA_TYPES.OBJECT && !updated.nestedProperties) {
						updated.nestedProperties = [];
						// Auto-expand when creating object type
						setExpandedProperties((prev) => new Set(prev).add(id));
					}
					return updated;
				}
				return p;
			}),
		);
	}, []);

	const handleAddEnumValue = useCallback(
		(path: string[], property: PropertyRow) => {
			if (!enumInputValue.trim()) return;

			const value = property.type === JSON_SCHEMA_TYPES.NUMBER || property.type === JSON_SCHEMA_TYPES.INTEGER ? Number(enumInputValue) : enumInputValue;

			if (path.length === 1) {
				handlePropertyChange(path[0], 'enumValues', [...property.enumValues, value]);
			} else {
				handleNestedPropertyChange(path, 'enumValues', [...property.enumValues, value]);
			}
			setEnumInputValue('');
		},
		[enumInputValue, handlePropertyChange, handleNestedPropertyChange],
	);

	const handleDeleteEnumValue = useCallback(
		(path: string[], property: PropertyRow, index: number) => {
			const newEnumValues = [...property.enumValues];
			newEnumValues.splice(index, 1);

			if (path.length === 1) {
				handlePropertyChange(path[0], 'enumValues', newEnumValues);
			} else {
				handleNestedPropertyChange(path, 'enumValues', newEnumValues);
			}
		},
		[handlePropertyChange, handleNestedPropertyChange],
	);

	const setPropertyRowFields = useCallback(
		(path: string[], values: Partial<PropertyRow>) => {
			Object.entries(values).forEach(([field, value]) => {
				if (path.length === 1) {
					handlePropertyChange(path[0], field as keyof PropertyRow, value);
				} else {
					handleNestedPropertyChange(path, field as keyof PropertyRow, value);
				}
			});
		},
		[handlePropertyChange, handleNestedPropertyChange],
	);

	const handleEnumSelected = useCallback(
		(path: string[], property: PropertyRow, enumModel: EnumModel | undefined, context?: ContextMetadataModel | undefined) => {
			if (!enumModel) {
				setPropertyRowFields(path, { enumRef: undefined });
				setEnumPickerPath(null);
				return;
			}

			if (!Array.isArray(enumModel.items)) {
				setEnumPickerPath(null);
				return;
			}

			const enumValues =
				property.type === JSON_SCHEMA_TYPES.NUMBER || property.type === JSON_SCHEMA_TYPES.INTEGER
					? enumModel.items.map((item) => item.value)
					: enumModel.items.map((item) => item.name);

			setPropertyRowFields(path, {
				enumRef: {
					id: enumModel.id,
					name: enumModel.name,
					contextId: enumModel.contextId || context?.id,
					contextName: context?.name,
				},
				enumValues,
			});
			setEnumPickerPath(null);
		},
		[setPropertyRowFields],
	);

	const handleClearEnumRef = useCallback(
		(path: string[], property: PropertyRow) => {
			setPropertyRowFields(path, { enumRef: undefined, enumValues: [] });
			if (editingEnumProperty === property.id) {
				setEditingEnumProperty(null);
				setEnumInputValue('');
			}
		},
		[editingEnumProperty, setPropertyRowFields],
	);

	// Helper function to convert PropertyRow[] to JsonSchema format
	const convertPropertiesToSchema = useCallback((props: PropertyRow[]): { properties: Record<string, JsonSchemaModel>; required: string[] } => {
		const properties: Record<string, JsonSchemaModel> = {};
		const required: string[] = [];

		props.forEach((prop) => {
			const trimmedName = prop.name.trim();
			if (!trimmedName) return;

			let propertySchema: JsonSchemaModel = {
				type: prop.type,
			};

			if (prop.description) {
				propertySchema.description = prop.description;
			}

			if (prop.format) {
				propertySchema.format = prop.format;
			}

			if (prop.enumValues.length > 0) {
				propertySchema.enum = prop.enumValues;
			}

			if (prop.enumRef) {
				propertySchema['x-enumRef'] = prop.enumRef;
			}

			// Handle nested object properties
			if (getNonNullType(prop.type) === JSON_SCHEMA_TYPES.OBJECT && prop.nestedProperties && prop.nestedProperties.length > 0) {
				const nested = convertPropertiesToSchema(prop.nestedProperties);
				propertySchema.properties = nested.properties;
				if (nested.required.length > 0) {
					propertySchema.required = nested.required;
				}
			}

			// Wrap in array if isMultiple is true
			if (prop.isMultiple) {
				propertySchema = writeSchemaEditorCollection(propertySchema, prop.isNullableCollection);
				if (prop.description) {
					propertySchema.description = prop.description;
				}
			}

			properties[trimmedName] = propertySchema;

			if (prop.isRequired) {
				required.push(trimmedName);
			}
		});

		return { properties, required };
	}, []);

	// Helper function to build the complete JSON schema
	const buildJsonSchema = useCallback((): SchemaValueModel => {
		const result = convertPropertiesToSchema(properties);

		return {
			Id: schemaId,
			Name: title || schemaValue?.Name || 'Schema',
			Value: {
				type: JSON_SCHEMA_TYPES.OBJECT,
				// Only include title/description if they have a value — omitting empty strings keeps the schema clean
				...(title && { title }),
				...(description && { description }),
				properties: result.properties,
				required: result.required,
			},
		};
	}, [properties, title, description, convertPropertiesToSchema, schemaId, schemaValue?.Name]);

	// Validation function
	const validateProperties = useCallback((props: PropertyRow[]): { isValid: boolean; errors: string[] } => {
		const errors: string[] = [];

		// Must have at least one property
		if (props.length === 0) {
			errors.push('At least one property is required');
			return { isValid: false, errors };
		}

		const validatePropsRecursive = (properties: PropertyRow[], path: string = '') => {
			const nameCount = new Map<string, number>();

			// Check for empty names and count duplicates
			properties.forEach((p) => {
				const trimmedName = p.name.trim();
				const fullPath = path ? `${path}.${trimmedName || '(empty)'}` : trimmedName || '(empty)';

				if (!trimmedName) {
					errors.push(`Property at ${fullPath} has an empty name`);
				} else {
					nameCount.set(trimmedName, (nameCount.get(trimmedName) || 0) + 1);
				}

				// Recursively validate nested properties
				if (p.nestedProperties && p.nestedProperties.length > 0) {
					validatePropsRecursive(p.nestedProperties, fullPath);
				}
			});

			// Check for duplicates
			nameCount.forEach((count, name) => {
				if (count > 1) {
					const fullPath = path ? `${path}.${name}` : name;
					errors.push(`Duplicate property name: ${fullPath}`);
				}
			});
		};

		validatePropsRecursive(props);

		return { isValid: errors.length === 0, errors };
	}, []);

	const getValidatedSchema = useCallback(() => {
		const validation = validateProperties(properties);

		if (!validation.isValid) {
			toastService.error(validation.errors.join(', '));
			return undefined;
		}

		return buildJsonSchema();
	}, [validateProperties, properties, buildJsonSchema, toastService]);

	const handleSave = useCallback(() => {
		const newSchemaValue = getValidatedSchema();
		if (!newSchemaValue) return;

		onSchemaValueChange(newSchemaValue);
		setOpen(false);
	}, [getValidatedSchema, onSchemaValueChange, setOpen]);

	const applySharedSchemaAction = useCallback((action: (schema: SchemaValueModel) => void) => {
		const newSchemaValue = getValidatedSchema();
		if (!newSchemaValue) return;
		action(newSchemaValue);
		setOpen(false);
	}, [getValidatedSchema, setOpen]);

	const handleCancel = useCallback(() => {
		setOpen(false);
	}, [setOpen]);

	// Unified handlers for cell changes
	const handleTypeChange = useCallback(
		(path: string[], level: number, type: JsonSchemaModel['type'], format?: string) => {
			if (level === 0) {
				handlePropertyChange(path[0], 'type', type);
				handlePropertyChange(path[0], 'format', format);
				// Clear enum values if type doesn't support them
				const tempProp: PropertyRow = { type, format } as PropertyRow;
				if (!canHaveEnum(tempProp)) {
					handlePropertyChange(path[0], 'enumValues', []);
					handlePropertyChange(path[0], 'enumRef', undefined);
					if (editingEnumProperty === path[0]) {
						setEditingEnumProperty(null);
						setEnumInputValue('');
					}
				}
			} else {
				handleNestedPropertyChange(path, 'type', type);
				handleNestedPropertyChange(path, 'format', format);
				// Clear enum values if type doesn't support them
				const tempProp: PropertyRow = { type, format } as PropertyRow;
				if (!canHaveEnum(tempProp)) {
					handleNestedPropertyChange(path, 'enumValues', []);
					handleNestedPropertyChange(path, 'enumRef', undefined);
					if (editingEnumProperty === path[path.length - 1]) {
						setEditingEnumProperty(null);
						setEnumInputValue('');
					}
				}
			}
		},
		[handlePropertyChange, handleNestedPropertyChange, editingEnumProperty],
	);

	const handleMultipleChange = useCallback(
		(path: string[], level: number, value: boolean) => {
			if (level === 0) {
				handlePropertyChange(path[0], 'isMultiple', value);
			} else {
				handleNestedPropertyChange(path, 'isMultiple', value);
			}
		},
		[handlePropertyChange, handleNestedPropertyChange],
	);

	const handleRequiredChange = useCallback(
		(path: string[], level: number, value: boolean) => {
			if (level === 0) {
				handlePropertyChange(path[0], 'isRequired', value);
			} else {
				handleNestedPropertyChange(path, 'isRequired', value);
			}
		},
		[handlePropertyChange, handleNestedPropertyChange],
	);

	const handleDescriptionChange = useCallback(
		(path: string[], level: number, value: string) => {
			if (level === 0) {
				handlePropertyChange(path[0], 'description', value);
			} else {
				handleNestedPropertyChange(path, 'description', value);
			}
		},
		[handlePropertyChange, handleNestedPropertyChange],
	);

	// Unified handler for name changes
	const handleNameChange = useCallback(
		(path: string[], level: number, value: string) => {
			if (level === 0) {
				handlePropertyChange(path[0], 'name', value);
			} else {
				handleNestedPropertyChange(path, 'name', value);
			}
		},
		[handlePropertyChange, handleNestedPropertyChange],
	);

	// Unified handler for delete operations
	const handleDelete = useCallback(
		(path: string[], level: number, propertyId: string) => {
			if (level === 0) {
				handleDeleteProperty(propertyId);
			} else {
				handleDeleteNestedProperty(path);
			}
		},
		[handleDeleteProperty, handleDeleteNestedProperty],
	);

	const handleRegenerateId = useCallback(() => {
		setSchemaId(uuid());
	}, []);

	return (
		<Dialog
			open={open}
			onClose={handleCancel}
			fullScreen={isMaximized}
			fullWidth={true}
			maxWidth={false}
			PaperProps={{
				ref: paperRef,
				sx: isMaximized
					? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', resize: 'none', overflow: 'hidden' }
					: { width: storedSize.width, height: storedSize.height, maxWidth: '95vw', maxHeight: '90vh', resize: 'both', overflow: 'hidden' },
			}}
		>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				Schema Object Designer
				<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
					<DialogHeaderActions isMaximized={isMaximized} onToggleMaximized={() => setIsMaximized((value) => !value)} onClose={handleCancel}>
						{properties.length > 0 && (
							<CopyToClipboardButton
								onClick={() => {
									const { Value } = buildJsonSchema();
									return JSON.stringify(Value, null, 2);
								}}
								title="Copy JSON Schema to clipboard"
								size="medium"
								edge={false}
								iconSize={20}
							/>
						)}
					</DialogHeaderActions>
				</Box>
			</DialogTitle>
			<DialogContent>
				{sharedSchemaActions && (sharedSchemaActions.schemas.length > 0 || sharedSchemaActions.onMakeIndependentCopy || sharedSchemaActions.onSaveAsSharedSchema) && (
					<Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1} sx={{ mt: 1, mb: 2 }}>
						{sharedSchemaActions.schemas.length > 0 && (
							<TextField select size="small" label="Use shared schema"
								value={sharedSchemaActions.schemas.some((schema) => schema.Id === sharedSchemaActions.selectedSchemaId) ? sharedSchemaActions.selectedSchemaId : ''}
								onChange={(event) => {
									sharedSchemaActions.onSelect(event.target.value);
									setOpen(false);
								}}
								sx={{ minWidth: 220 }}>
								<MenuItem value="" disabled>Choose a shared schema</MenuItem>
								{sharedSchemaActions.schemas.map((schema) => (
									<MenuItem key={schema.Id} value={schema.Id}>{schema.Name || schema.Value.title || schema.Id}</MenuItem>
								))}
							</TextField>
						)}
						{sharedSchemaActions.onMakeIndependentCopy && (
							<Button size="small" onClick={() => applySharedSchemaAction(sharedSchemaActions.onMakeIndependentCopy!)}>Make independent copy</Button>
						)}
						{sharedSchemaActions.onSaveAsSharedSchema && (
							<Button size="small" onClick={() => applySharedSchemaAction(sharedSchemaActions.onSaveAsSharedSchema!)}>Save as shared schema</Button>
						)}
					</Stack>
				)}
				<Box sx={{ mb: 2, mt: 1 }}>
					<Button
						size="small"
						onClick={() => setDetailsExpanded(!detailsExpanded)}
						startIcon={detailsExpanded ? <ExpandLess /> : <ExpandMore />}
						sx={{ mb: 1 }}
					>
						Schema Details
					</Button>
					<Collapse in={detailsExpanded}>
						<Box sx={{ mb: 2 }}>
							<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
								<TextField label="Schema Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth margin="normal" variant="outlined" />
								<TextField
									label="Schema ID"
									value={schemaId}
									fullWidth
									margin="normal"
									variant="outlined"
									InputProps={{
										readOnly: true,
										endAdornment: (
											<InputAdornment position="end">
												<CopyToClipboardButton onClick={() => schemaId} title="Copy Schema ID" size="small" edge={false} iconSize={18} />
												<Tooltip title="Regenerate Schema ID">
													<IconButton size="small" onClick={handleRegenerateId}>
														<Refresh fontSize="small" />
													</IconButton>
												</Tooltip>
											</InputAdornment>
										),
									}}
								/>
							</Box>
							<TextField
								label="Schema Description"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								fullWidth
								margin="normal"
								variant="outlined"
								multiline
								rows={2}
							/>
						</Box>
					</Collapse>
				</Box>

				<TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, backgroundColor: 'background.paper' }}>
					<Table size="small" sx={{ '& .MuiTableHead-root': { backgroundColor: 'action.hover' }, '& .MuiTableBody-root > .MuiTableRow-root > .MuiTableCell-root': { py: 1.1 } }}>
						<TableHead>
							<TableRow>
								<TableCell>Property Name</TableCell>
								<TableCell>Type</TableCell>
								<TableCell sx={{ width: 76, minWidth: 76, maxWidth: 76, whiteSpace: 'nowrap', px: 0.5 }}>Multiple</TableCell>
								<TableCell sx={{ width: 76, minWidth: 76, maxWidth: 76, whiteSpace: 'nowrap', px: 0.5 }}>Required</TableCell>
								<TableCell width="25%">Description</TableCell>
								<TableCell>Enum Values</TableCell>
								<TableCell sx={{ width: 56, minWidth: 56, maxWidth: 56, whiteSpace: 'nowrap', px: 0.25 }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							<PropertyRows
								properties={properties}
								parentPath={[]}
								level={0}
								expandedProperties={expandedProperties}
								onToggleExpanded={toggleExpanded}
								onNameChange={handleNameChange}
								onTypeChange={handleTypeChange}
								onMultipleChange={handleMultipleChange}
								onRequiredChange={handleRequiredChange}
								onDescriptionChange={handleDescriptionChange}
								editingEnumProperty={editingEnumProperty}
								enumInputValue={enumInputValue}
								onEnumInputChange={setEnumInputValue}
								onEditingChange={setEditingEnumProperty}
								onAddEnum={handleAddEnumValue}
								onDeleteEnum={handleDeleteEnumValue}
								enumPickerPath={enumPickerPath}
								onOpenEnumPicker={setEnumPickerPath}
								onEnumSelected={handleEnumSelected}
								onClearEnumRef={handleClearEnumRef}
								onDelete={handleDelete}
								onReorder={handleReorderProperty}
								dragOverPropertyId={dragOverPropertyId}
								onDragOverProperty={setDragOverPropertyId}
								onClearDragOverProperty={() => setDragOverPropertyId(null)}
								onAddProperty={handleAddProperty}
							/>
							<TableRow>
								<TableCell colSpan={7}>
									<Button startIcon={<AddCircle />} onClick={() => handleAddProperty()} variant="outlined" size="small">
										Add Property
									</Button>
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancel}>Cancel</Button>
				<Button onClick={handleSave} variant="contained" color="primary">
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
}
